import { Calendar } from 'lucide-react';
import { type ComponentProps, useEffect, useState } from 'react';
import { cn } from '../lib/cn';
import { useFieldControl } from './Field';
import { controlClasses, controlSizes } from './Input';

/**
 * Data biznesowa "YYYY-MM-DD" (Europe/Warsaw). Natywny kalendarz przeglądarki (dobry na mobile),
 * wartość zawsze w formacie ISO.
 */
export function DateInput({
  value,
  onValueChange,
  size = 'm',
  className,
  ...props
}: Omit<ComponentProps<'input'>, 'value' | 'onChange' | 'type' | 'size'> & {
  value: string;
  onValueChange: (value: string) => void;
  size?: keyof typeof controlSizes;
}) {
  const field = useFieldControl(props);
  return (
    <div className="relative w-full">
      <Calendar
        size={16}
        strokeWidth={1.75}
        aria-hidden
        className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-fg-muted"
      />
      <input
        type="date"
        lang="pl-PL"
        {...props}
        {...field}
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        className={cn(controlClasses, controlSizes[size], 'pl-9 tabular', className)}
      />
    </div>
  );
}

/** "830" → "08:30", "8:3" → "08:03", "1630" → "16:30"; null = nie da się odczytać. */
export function normalizeTime(raw: string): string | null {
  const text = raw.trim().replace(/[.,;h]/g, ':');
  if (!text) return null;
  let h: number;
  let m: number;
  if (text.includes(':')) {
    const [hh = '', mm = ''] = text.split(':');
    h = Number(hh);
    m = mm === '' ? 0 : Number(mm.padEnd(2, '0').slice(0, 2));
  } else if (/^\d{1,2}$/.test(text)) {
    h = Number(text);
    m = 0;
  } else if (/^\d{3,4}$/.test(text)) {
    h = Number(text.slice(0, text.length - 2));
    m = Number(text.slice(-2));
  } else {
    return null;
  }
  if (!Number.isInteger(h) || !Number.isInteger(m) || h < 0 || h > 23 || m < 0 || m > 59) return null;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Godzina "HH:MM" w foncie mono (Figma: pola od–do we wpisie czasu). Przyjmuje skróty („830”, „8:30”, „16”),
 * normalizuje przy opuszczeniu pola.
 */
export function TimeInput({
  value,
  onValueChange,
  size = 's',
  className,
  onBlur,
  ...props
}: Omit<ComponentProps<'input'>, 'value' | 'onChange' | 'type' | 'size'> & {
  value: string;
  onValueChange: (value: string) => void;
  size?: keyof typeof controlSizes;
}) {
  const [draft, setDraft] = useState(value);
  const field = useFieldControl(props);
  useEffect(() => setDraft(value), [value]);

  return (
    <input
      type="text"
      inputMode="numeric"
      autoComplete="off"
      placeholder="00:00"
      maxLength={5}
      {...props}
      {...field}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={(e) => {
        const normalized = normalizeTime(draft);
        if (normalized) {
          setDraft(normalized);
          if (normalized !== value) onValueChange(normalized);
        } else if (draft.trim() === '') {
          onValueChange('');
        } else {
          setDraft(value);
        }
        onBlur?.(e);
      }}
      className={cn(
        controlClasses,
        controlSizes[size],
        'w-[72px] text-center font-mono font-medium tabular',
        className,
      )}
    />
  );
}
